import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, IconButton, InputAdornment, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import { useMutation } from "@apollo/client";
import * as yup from "yup";
import CountryList, { Country } from "country-list-with-dial-code-and-flag";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { hooks } from "../../hooks";
import { utils } from "../../utils";
import { components } from "../../components";
import AppTextField from "../../components/apps/AppTextField";
import { AppSubmitButton } from "../../components/apps";
import { REGISTER } from "../../graphql/mutations/auth";
import { APP_ROUTES } from "../../constants/app_routes";
import { showErrorToast } from "../../components/apps/AppError";
import AppSelectCountryField from "../../components/apps/AppSelectCountryField";
import { currentBranch } from "../../constants/constants";

export const SignUpScreen: React.FC = () => {
  const navigate = hooks.useNavigate();
  const { t } = useTranslation();
  const DEFAULT_COUNTRY = "TH";
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { pathname } = useLocation();
  const countries = useMemo(() => CountryList.getAll(), []);
  const mapCountries = countries.map((c: Country) => {
    return {
      label: c.name,
      value: c.code,
      flag: c.flag,
    };
  });

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);

  const renderHeader = (): JSX.Element => {
    return <components.Header title={t("register")} goBack={true} />;
  };

  const validationSchema = yup.object({
    emailOrPhone: yup
      .string()
      .required(t("emailOrPhoneRequired"))
      .test("email-or-phone", t("invalidEmailOrPhone"), (value) => {
        if (!value) return true;
        if (value.includes("@")) {
          return yup.string().email().isValidSync(value);
        }
        const phoneRegex = /^(\+)?(\d{1,3})(\d{6,14})$/;
        return phoneRegex.test(value.replace(/[\s\-()]/g, ""));
      }),
    password: yup
      .string()
      .required(t("passwordRequired"))
      .min(6, t("minCharactor")),
  });

  const [register, { loading }] = useMutation(REGISTER, {
    onCompleted: (data: any) => {
      if (data?.registerMember) {
        navigate(APP_ROUTES.VERIFICATION);
      }
    },
    onError(err) {
      showErrorToast(err.message);
    },
  });

  function handleSubmit(data: any) {
    register({
      variables: {
        input: {
          emailOrPhone: data.emailOrPhone,
          password: data.password,
          country,
          branch: currentBranch,
        },
      },
    });
  }

  const renderDescription = (): JSX.Element => {
    return (
      <div style={{ ...utils.rowCenter(), marginBottom: 30, color: "white" }}>
        <Typography variant="h6">{t("createAccount")}</Typography>
      </div>
    );
  };

  const renderInputFields = (): JSX.Element => {
    return (
      <Formik
        validateOnChange
        initialValues={{ emailOrPhone: "", password: "" }}
        onSubmit={(data: any) => handleSubmit(data)}
        validationSchema={validationSchema}
      >
        {() => (
          <Form noValidate autoComplete="off">
            <Box sx={{ mb: { xs: 5, xl: 8 } }}>
              <AppSelectCountryField
                options={mapCountries}
                label={t("residentialAddress")}
                name="country"
                theme="dark"
                defaultValue={DEFAULT_COUNTRY}
                onChange={(e) => {
                  setCountry(e);
                }}
              />
              <AppTextField
                placeholder={t("emailOrPhone")}
                name="emailOrPhone"
                label={t("emailOrPhone")}
                themeColor="dark"
              />
              <AppTextField
                placeholder={t("minCharactor")}
                name="password"
                label={t("password")}
                themeColor="dark"
                type={showPassword ? "text" : "password"}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          sx={{ color: "grey" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon color="inherit" />
                          ) : (
                            <VisibilityIcon color="inherit" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            <AppSubmitButton
              label={t("register")}
              disabled={false}
              loading={loading}
            />
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <main className="container register" style={{ paddingBottom: 50 }}>
      {renderHeader()}
      {renderDescription()}
      {renderInputFields()}
    </main>
  );
};
